import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, Heart, ChevronRight, Loader2, 
  Star, CheckCircle, Clock, User, MapPin,
  DollarSign, MessageCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from '../lib/toast';
import { formatDistanceToNow } from 'date-fns';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Normalize image URLs
const normalizeImageUrl = (url) => {
  if (!url) return null;
  if (typeof url === 'object' && url.url) return url.url;
  if (typeof url !== 'string') return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${API_BASE_URL}${url}`;
  return url;
};

export default function ItemMatchesPage() {
  const { itemId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [item, setItem] = useState(null);
  const [matches, setMatches] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });

  useEffect(() => {
    fetchMatches();
  }, [itemId]);

  const fetchMatches = async (page = 1) => {
    setLoading(true);
    try {
      const res = await api.get(`/matches/for-inventory/${itemId}?page=${page}&limit=20`);
      setItem(res.data.item);
      setMatches(res.data.matches);
      setPagination({
        page: res.data.page,
        total: res.data.total,
        totalPages: res.data.total_pages
      });
    } catch (error) {
      console.error('Failed to fetch matches:', error);
      toast.error('Failed to load matches');
      navigate('/my-items');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (min, max) => {
    if (min && max && min !== max) {
      return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
    }
    if (max) return `Up to $${max.toLocaleString()}`;
    if (min) return `From $${min.toLocaleString()}`;
    return 'Price negotiable';
  };

  const getTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#1E3A5F]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto" data-testid="item-matches-page">
        {/* Back button & Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/my-items')}
            className="flex items-center gap-2 text-sm text-[var(--theme-text-muted)] hover:text-[var(--theme-text-primary)] mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to My Items
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[var(--theme-text-primary)]">
                Interested Buyers
              </h1>
              <p className="text-sm text-[var(--theme-text-muted)]">
                {pagination.total} {pagination.total === 1 ? 'buyer' : 'buyers'} interested in "{item?.title}"
              </p>
            </div>
            <Link to={`/item/${itemId}`}>
              <Button variant="outline" className="rounded-full">
                View Item
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Matches List */}
        {matches.length > 0 ? (
          <div className="space-y-4">
            {matches.map((match) => (
              <div 
                key={match.match_id}
                className="bg-[var(--theme-card)] rounded-xl border border-[var(--theme-border)] hover:border-[var(--theme-border-hover)] transition-all overflow-hidden"
                data-testid={`match-item-${match.match_id}`}
              >
                <div className="flex flex-col sm:flex-row">
                  {/* Want Image */}
                  <Link 
                    to={`/wtb/${match.wtb.id}`}
                    className="w-full sm:w-32 h-32 sm:h-auto flex-shrink-0"
                  >
                    {match.wtb.images?.[0] ? (
                      <img 
                        src={normalizeImageUrl(match.wtb.images[0])}
                        alt={match.wtb.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-[var(--theme-hover)] flex items-center justify-center">
                        <Heart className="w-8 h-8 text-[var(--theme-text-muted)]" />
                      </div>
                    )}
                  </Link>

                  {/* Want Details */}
                  <div className="flex-1 p-4">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div className="flex-1">
                        {/* Title & Status */}
                        <div className="flex items-start gap-2 mb-2">
                          <Link 
                            to={`/wtb/${match.wtb.id}`}
                            className="font-semibold text-lg text-[var(--theme-text-primary)] hover:text-[#1E3A5F] dark:hover:text-[#C9956C] transition-colors line-clamp-1"
                          >
                            {match.wtb.title}
                          </Link>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {match.has_special_interest && (
                              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                <Star className="w-3 h-3 fill-current" />
                                High Interest
                              </span>
                            )}
                            {match.accepted_by_owner && (
                              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                <CheckCircle className="w-3 h-3" />
                                Accepted
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Price Range */}
                        <p className="text-lg font-bold text-[#059669] mb-2">
                          {formatPrice(match.wtb.min_price, match.wtb.max_price)}
                        </p>

                        {/* Meta info */}
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[var(--theme-text-muted)]">
                          {match.wtb.condition && (
                            <span className="capitalize">{match.wtb.condition.replace('_', ' ')} condition</span>
                          )}
                          {match.wtb.location_name && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3.5 h-3.5" />
                              {match.wtb.location_name}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {getTimeAgo(match.created_at)}
                          </span>
                        </div>
                      </div>

                      {/* Buyer Info & Actions */}
                      <div className="flex sm:flex-col items-center sm:items-end gap-3">
                        {/* Buyer */}
                        <Link 
                          to={`/profile/${match.wanter.id}`}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--theme-hover)] hover:bg-[var(--theme-border)] transition-colors"
                        >
                          <div className="w-8 h-8 rounded-full bg-[#1E3A5F]/10 dark:bg-[#C9956C]/10 flex items-center justify-center">
                            <User className="w-4 h-4 text-[#1E3A5F] dark:text-[#C9956C]" />
                          </div>
                          <div className="text-left">
                            <p className="text-sm font-medium text-[var(--theme-text-primary)]">
                              @{match.wanter.username}
                            </p>
                            <p className="text-xs text-[var(--theme-text-muted)]">
                              Trust: {match.wanter.trust_score || 0}%
                            </p>
                          </div>
                        </Link>

                        {/* Actions */}
                        <div className="flex gap-2">
                          {match.accepted_by_owner ? (
                            <Link to={`/messages/${match.match_id}`}>
                              <Button size="sm" className="rounded-full bg-[#1E3A5F] hover:bg-[#152d4a]">
                                <MessageCircle className="w-4 h-4 mr-1" />
                                Message
                              </Button>
                            </Link>
                          ) : (
                            <Link to={`/matches/${match.match_id}`}>
                              <Button size="sm" className="rounded-full bg-[#C9956C] hover:bg-[#B8845B]">
                                View Match
                                <ChevronRight className="w-4 h-4 ml-1" />
                              </Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-[var(--theme-card)] rounded-2xl border border-[var(--theme-border)]">
            <div className="w-16 h-16 rounded-full bg-[var(--theme-hover)] flex items-center justify-center mx-auto mb-4">
              <Heart className="w-8 h-8 text-[var(--theme-text-muted)]" />
            </div>
            <h3 className="text-xl font-bold text-[var(--theme-text-primary)] mb-2">
              No Interested Buyers Yet
            </h3>
            <p className="text-[var(--theme-text-secondary)] max-w-md mx-auto">
              When buyers post Wants that match your item, they'll appear here. 
              Keep your listing active to maximize visibility!
            </p>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            <Button
              variant="outline"
              onClick={() => fetchMatches(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="rounded-full"
            >
              Previous
            </Button>
            <span className="flex items-center px-4 text-sm text-[var(--theme-text-muted)]">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              onClick={() => fetchMatches(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className="rounded-full"
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
